import React, { useState, useEffect } from 'react';
import api from '../api/axios';

const Agenda = () => {
    const [agendaItems, setAgendaItems] = useState([]);
    const [speakers, setSpeakers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [currentItem, setCurrentItem] = useState({
        title: '', date: '', start_time: '', end_time: '', speaker_id: '', moderator: '', status: 'active',
        block_name: '', block_color: '#9d4edd', activity_type: 'session', country: '', group_name: ''
    });
    const [isEditing, setIsEditing] = useState(false);

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const [agendaRes, speakersRes] = await Promise.all([
                api.get('/agenda'),
                api.get('/speakers?status=active')
            ]);
            setAgendaItems(agendaRes.data);
            setSpeakers(speakersRes.data);
        } catch (error) {
            console.error("Error fetching data", error);
        } finally {
            setIsLoading(false);
        }
    };

    const handleSave = async (e) => {
        e.preventDefault();
        try {
            const dataToSave = { ...currentItem, speaker_id: currentItem.speaker_id || null };
            if (isEditing) {
                await api.put(`/agenda/${currentItem.id}`, dataToSave);
            } else {
                await api.post('/agenda', dataToSave);
            }
            fetchData();
            closeModal();
        } catch (error) {
            console.error("Error saving agenda item", error);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("¿Estás seguro de eliminar esta charla de la agenda?")) {
            try {
                await api.delete(`/agenda/${id}`);
                fetchData();
            } catch (error) {
                console.error("Error deleting", error);
            }
        }
    };

    const openModal = (item = null) => {
        if (item) {
            // Format time and date for inputs
            setCurrentItem({
                ...item,
                date: item.date ? item.date.split('T')[0] : '',
                start_time: item.start_time,
                end_time: item.end_time,
                speaker_id: item.speaker_id || '',
                block_name: item.block_name || '',
                block_color: item.block_color || '#9d4edd',
                activity_type: item.activity_type || 'session',
                country: item.country || '',
                group_name: item.group_name || ''
            });
            setIsEditing(true);
        } else {
            setCurrentItem({
                title: '', date: '', start_time: '', end_time: '', speaker_id: '', moderator: '', status: 'active',
                block_name: '', block_color: '#9d4edd', activity_type: 'session', country: '', group_name: ''
            });
            setIsEditing(false);
        }
        setIsModalOpen(true);
    };

    const closeModal = () => setIsModalOpen(false);

    return (
        <div className="animate-fadeIn">
            <header className="flex justify-between items-center mb-10">
                <div>
                    <h1 className="text-4xl font-extrabold text-slate-900 tracking-tight">Cronograma de Agenda</h1>
                    <p className="text-slate-500 mt-2">Organiza las charlas y eventos del congreso.</p>
                </div>
                <button
                    onClick={() => openModal()}
                    className="bg-indigo-600 text-white px-6 py-3 rounded-2xl font-bold shadow-lg shadow-indigo-900/20 hover:bg-indigo-500 transition-all flex items-center transform hover:scale-105 active:scale-95"
                >
                    <span className="mr-2 text-xl">+</span> Nueva Charla
                </button>
            </header>

            {isLoading ? (
                <div className="text-center py-20 text-slate-400">Cargando cronograma...</div>
            ) : (
                <div className="bg-white rounded-[40px] shadow-sm border border-slate-100 overflow-hidden">
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-slate-50 border-b border-slate-100">
                                <th className="p-6 text-xs font-bold uppercase tracking-widest text-slate-400">Título / Tema</th>
                                <th className="p-6 text-xs font-bold uppercase tracking-widest text-slate-400">Fecha y Hora</th>
                                <th className="p-6 text-xs font-bold uppercase tracking-widest text-slate-400">Speaker / Moderador</th>
                                <th className="p-6 text-xs font-bold uppercase tracking-widest text-slate-400 text-center">Estado</th>
                                <th className="p-6 text-xs font-bold uppercase tracking-widest text-slate-400 text-right">Acciones</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-50">
                            {agendaItems.map((item) => (
                                <tr key={item.id} className="hover:bg-slate-50/50 transition-colors group">
                                    <td className="p-6">
                                        <div className="font-bold text-slate-900 leading-snug">{item.title}</div>
                                    </td>
                                    <td className="p-6">
                                        <div className="text-sm font-semibold text-slate-700">{item.date ? new Date(item.date).toLocaleDateString() : 'TBD'}</div>
                                        <div className="text-xs text-slate-400 font-medium mt-1">{item.start_time.substring(0, 5)} - {item.end_time.substring(0, 5)}</div>
                                    </td>
                                    <td className="p-6">
                                        <div className="text-sm font-bold text-indigo-600">{item.speaker_name || 'Sin Speaker'}</div>
                                        <div className="text-xs text-slate-400 font-medium mt-1">Mod: {item.moderator || 'N/A'}</div>
                                    </td>
                                    <td className="p-6 text-center">
                                        <span className={`px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-widest ${item.status === 'active' ? 'bg-emerald-100 text-emerald-600' : 'bg-red-100 text-red-600'}`}>
                                            {item.status}
                                        </span>
                                    </td>
                                    <td className="p-6 text-right">
                                        <div className="flex justify-end space-x-2 opacity-1 group-hover:opacity-100 transition-opacity">
                                            <button
                                                onClick={() => openModal(item)}
                                                className="p-2.5 rounded-xl bg-slate-100 text-slate-600 hover:bg-indigo-100 hover:text-indigo-600 transition-all shadow-sm"
                                            >
                                                ✏️
                                            </button>
                                            <button
                                                onClick={() => handleDelete(item.id)}
                                                className="p-2.5 rounded-xl bg-red-50 text-red-500 hover:bg-red-100 transition-all shadow-sm"
                                            >
                                                🗑️
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Modal */}
            {isModalOpen && (
                <div className="fixed inset-0 bg-slate-900/90 backdrop-blur-md flex items-center justify-center p-6 z-[100] animate-fadeIn">
                    <div className="bg-white rounded-[40px] shadow-2xl w-full max-w-2xl overflow-y-auto max-h-[90vh] animate-slideUp">
                        <div className="bg-gradient-to-r from-indigo-700 to-blue-600 p-8 text-white relative sticky top-0 z-10">
                            <h2 className="text-3xl font-black">{isEditing ? 'Editar Charla' : 'Agregar Charla'}</h2>
                            <button onClick={closeModal} className="absolute top-8 right-8 text-white/50 hover:text-white text-2xl">✕</button>
                        </div>
                        <form onSubmit={handleSave} className="p-10 grid grid-cols-2 gap-6">
                            <div className="col-span-2">
                                <label className="block text-slate-700 text-sm font-bold mb-2">Título de la Charla</label>
                                <input
                                    className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl focus:outline-none focus:ring-4 focus:ring-indigo-100 transition-all shadow-inner"
                                    value={currentItem.title}
                                    onChange={e => setCurrentItem({ ...currentItem, title: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="col-span-2 md:col-span-1">
                                <label className="block text-slate-700 text-sm font-bold mb-2">Fecha</label>
                                <input
                                    type="date"
                                    className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl focus:outline-none focus:ring-4 focus:ring-indigo-100 transition-all shadow-inner"
                                    value={currentItem.date}
                                    onChange={e => setCurrentItem({ ...currentItem, date: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="col-span-2 md:col-span-1">
                                <label className="block text-slate-700 text-sm font-bold mb-2">Speaker</label>
                                <select
                                    className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl focus:outline-none focus:ring-4 focus:ring-indigo-100 transition-all shadow-inner appearance-none"
                                    value={currentItem.speaker_id}
                                    onChange={e => setCurrentItem({ ...currentItem, speaker_id: e.target.value })}
                                >
                                    <option value="">Seleccionar Speaker...</option>
                                    {speakers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                                </select>
                            </div>
                            <div className="col-span-1">
                                <label className="block text-slate-700 text-sm font-bold mb-2">Hora Inicio</label>
                                <input
                                    type="time"
                                    className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl focus:outline-none focus:ring-4 focus:ring-indigo-100 transition-all shadow-inner"
                                    value={currentItem.start_time}
                                    onChange={e => setCurrentItem({ ...currentItem, start_time: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="col-span-1">
                                <label className="block text-slate-700 text-sm font-bold mb-2">Hora Fin</label>
                                <input
                                    type="time"
                                    className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl focus:outline-none focus:ring-4 focus:ring-indigo-100 transition-all shadow-inner"
                                    value={currentItem.end_time}
                                    onChange={e => setCurrentItem({ ...currentItem, end_time: e.target.value })}
                                    required
                                />
                            </div>
                            <div className="col-span-2 md:col-span-1">
                                <label className="block text-slate-700 text-sm font-bold mb-2">Moderador (Opcional)</label>
                                <input
                                    className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl focus:outline-none focus:ring-4 focus:ring-indigo-100 transition-all shadow-inner"
                                    value={currentItem.moderator}
                                    onChange={e => setCurrentItem({ ...currentItem, moderator: e.target.value })}
                                />
                            </div>
                            <div className="col-span-2">
                                <h3 className="text-sm font-bold text-slate-400 uppercase tracking-widest mb-4 border-b pb-2">Agrupación y Diseño</h3>
                            </div>
                            <div className="col-span-2 md:col-span-1">
                                <label className="block text-slate-700 text-sm font-bold mb-2">Grupo (Pestaña en la App)</label>
                                <input
                                    className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl focus:outline-none focus:ring-4 focus:ring-indigo-100 transition-all shadow-inner"
                                    value={currentItem.group_name}
                                    onChange={e => setCurrentItem({ ...currentItem, group_name: e.target.value })}
                                    placeholder="Ej: Día 1, Sala A, Track Técnico (Opcional)"
                                />
                            </div>
                            <div className="col-span-2 md:col-span-1">
                                <label className="block text-slate-700 text-sm font-bold mb-2">Nombre del Bloque (Ej: Bloque 1...)</label>
                                <input
                                    className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl focus:outline-none focus:ring-4 focus:ring-indigo-100 transition-all shadow-inner"
                                    value={currentItem.block_name}
                                    onChange={e => setCurrentItem({ ...currentItem, block_name: e.target.value })}
                                    placeholder="Opcional"
                                />
                            </div>
                            <div className="col-span-2 md:col-span-1">
                                <label className="block text-slate-700 text-sm font-bold mb-2">Color del Bloque</label>
                                <div className="flex gap-2">
                                    <input
                                        type="color"
                                        className="h-14 w-14 bg-slate-50 border border-slate-100 p-1 rounded-2xl cursor-pointer"
                                        value={currentItem.block_color}
                                        onChange={e => setCurrentItem({ ...currentItem, block_color: e.target.value })}
                                    />
                                    <input
                                        className="flex-grow bg-slate-50 border border-slate-100 p-4 rounded-2xl focus:outline-none focus:ring-4 focus:ring-indigo-100 transition-all shadow-inner font-mono"
                                        value={currentItem.block_color}
                                        onChange={e => setCurrentItem({ ...currentItem, block_color: e.target.value })}
                                    />
                                </div>
                            </div>
                            <div className="col-span-2 md:col-span-1">
                                <label className="block text-slate-700 text-sm font-bold mb-2">Tipo de Actividad</label>
                                <select
                                    className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl focus:outline-none focus:ring-4 focus:ring-indigo-100 transition-all shadow-inner appearance-none"
                                    value={currentItem.activity_type}
                                    onChange={e => setCurrentItem({ ...currentItem, activity_type: e.target.value })}
                                >
                                    <option value="session">Sesión Académica</option>
                                    <option value="breakfast">Desayuno</option>
                                    <option value="coffee">Coffee Break</option>
                                    <option value="lunch">Almuerzo / Cena</option>
                                    <option value="closing">Cierre / Clausura</option>
                                    <option value="other">Otro / Inauguración</option>
                                </select>
                            </div>
                            <div className="col-span-2 md:col-span-1">
                                <label className="block text-slate-700 text-sm font-bold mb-2">País (Opcional - Para Bandera)</label>
                                <input
                                    className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl focus:outline-none focus:ring-4 focus:ring-indigo-100 transition-all shadow-inner"
                                    value={currentItem.country}
                                    onChange={e => setCurrentItem({ ...currentItem, country: e.target.value })}
                                    placeholder="Colombia, México, etc."
                                />
                            </div>
                            <div className="col-span-2 md:col-span-1">
                                <label className="block text-slate-700 text-sm font-bold mb-2">Estado</label>
                                <select
                                    className="w-full bg-slate-50 border border-slate-100 p-4 rounded-2xl focus:outline-none focus:ring-4 focus:ring-indigo-100 transition-all shadow-inner appearance-none"
                                    value={currentItem.status}
                                    onChange={e => setCurrentItem({ ...currentItem, status: e.target.value })}
                                >
                                    <option value="active">Activo</option>
                                    <option value="inactive">Inactivo</option>
                                </select>
                            </div>
                            <div className="col-span-2 pt-6 flex justify-end space-x-4">
                                <button type="button" onClick={closeModal} className="px-8 py-4 text-slate-500 font-bold hover:text-slate-800 transition-colors">Cancelar</button>
                                <button type="submit" className="bg-indigo-600 text-white px-12 py-4 rounded-2xl font-black shadow-xl shadow-indigo-900/20 hover:bg-indigo-500 transition-all active:scale-95">
                                    {isEditing ? 'Actualizar Agenda' : 'Programar Charla'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Agenda;
